import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { toast } from "react-toastify";
import {
  BriefcaseBusiness,
  ChevronDown,
  Gift,
  ShieldCheck,
  Star,
  UserRound,
  ArrowRight,
  CircleUserRound,
  Phone,
  Clock3,
} from "lucide-react";
import API_URL from "../config/config";

const rightPanelBenefits = [
  {
    icon: ShieldCheck,
    title: "Secure & Safe",
    description: "Your data is protected",
  },
  {
    icon: Clock3,
    title: "Quick & Easy",
    description: "Sign up in seconds",
  },
  {
    icon: Star,
    title: "Trusted Delivery",
    description: "On-time, every time",
  },
];

const Signup = ({ showSignupModal, setShowSignupModal, setShowSigninModal, setShowOTPModal, setMobileForOTP, setOtpIntent }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm();
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("individual");

  useEffect(() => {
    if (!showSignupModal) {
      return undefined;
    }

    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = overflow;
    };
  }, [showSignupModal]);

  const mobileno = watch("mobileno", "");

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length > 10) return mobileno;
    if (digits.length > 5) {
      return `${digits.slice(0, 5)} ${digits.slice(5, 10)}`;
    }
    return digits;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setValue("mobileno", formatted, { shouldValidate: true });
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.referralCode) {
        const referralResponse = await axios.post(
          `${API_URL}/refer/referral/verify`,
          { referralCode: data.referralCode.trim(), userType: "customer" },
          { headers: { "Content-Type": "application/json" } }
        );

        if (referralResponse.data.error) {
          toast.error(referralResponse.data.message || "Invalid referral code");
          setLoading(false);
          return;
        }
      }

      const mobile = data.mobileno.replace(/\D/g, "");
      const response = await axios.post(`${API_URL}/otp/send-otp`, {
        mobile, accountType: "customer", purpose: "customer-auth", authMode: "signup",
      });
      if (!response.data.success) throw new Error(response.data.message || "Unable to send OTP");
      setOtpIntent({ authMode: "signup", fullname: data.fullname.trim(), usecase: type, referredBy: (data.referralCode || "").trim() });
      setMobileForOTP(mobile);
      setShowSignupModal(false);
      setShowOTPModal(true);
      toast.success("OTP sent to your mobile number");
    } catch (error) {
      console.error("Signup error:", error);
      if (error.response?.data?.message?.includes("duplicate key")) {
        toast.error("Mobile number already registered");
      } else {
        toast.error(error.response?.data?.message || error.message || "Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!showSignupModal) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-black/55 p-2 sm:p-3 lg:p-4">
      <div className="mx-auto flex min-h-full w-full items-center justify-center">
        <div className="relative w-full max-w-[1040px] rounded-[20px] bg-[#fffdf7] p-1.5 shadow-[0_24px_90px_rgba(0,0,0,0.28)] sm:rounded-[26px] sm:p-2">
          <button
            onClick={() => setShowSignupModal(false)}
            className="absolute right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-[28px] leading-none text-slate-500 shadow-[0_10px_24px_rgba(15,23,42,0.10)] transition hover:text-slate-700 sm:right-4 sm:top-4"
            aria-label="Close sign up form"
          >
            &times;
          </button>

          <div className="flex max-h-[calc(100dvh-0.75rem)] flex-col overflow-hidden rounded-[18px] border border-[#efe8d1] bg-white shadow-[0_16px_45px_rgba(167,139,84,0.10)] sm:max-h-[calc(100dvh-1rem)] sm:rounded-[24px] lg:max-h-[calc(100dvh-1.25rem)]">
            <div className="grid min-h-0 flex-1 lg:grid-cols-[58%_42%]">
              <div className="overflow-y-auto bg-[radial-gradient(circle_at_top,_rgba(255,255,255,1),_rgba(255,252,244,0.96)_58%,_rgba(255,248,228,0.9)_100%)] px-4 pb-4 pt-6 sm:px-5 sm:pt-7 lg:px-6 lg:pb-5 lg:pt-6">
                <div className="mx-auto max-w-[520px]">
                  <img
                    src="/assets/logo/logo.png"
                    alt="Instaport Delivery"
                    className="h-auto w-[132px] max-w-full sm:w-[160px] lg:w-[180px]"
                  />

                  <h2 className="mt-2.5 text-[24px] font-extrabold leading-[0.98] tracking-[-0.04em] text-[#071114] sm:text-[30px] lg:mt-3 lg:text-[34px]">
                    Sign Up
                  </h2>
                  <p className="mt-1 text-[11px] font-medium text-[#626b7b] sm:text-[13px] lg:text-[14px]">
                    Fast. Reliable. Delivered with care.
                  </p>
                  <div className="mt-2.5 h-[3px] w-[52px] rounded-full bg-[#f7b600]" />

                  <div className="mt-3.5 flex overflow-hidden rounded-[14px] border border-[#e6dfcf] bg-white shadow-[0_10px_24px_rgba(15,23,42,0.05)] lg:mt-4">
                    <button
                      type="button"
                      onClick={() => setType("individual")}
                      className={`flex flex-1 items-center justify-center gap-2 px-3 py-2 text-[11px] font-medium transition sm:gap-2.5 sm:px-4 sm:text-[13px] lg:px-4 lg:py-2.5 lg:text-[14px] ${
                        type === "individual"
                          ? "border border-[#f4b400] bg-[#fffdf4] text-[#21582e] shadow-[inset_0_-3px_0_#f4b400]"
                          : "text-[#121212]"
                      }`}
                    >
                      <CircleUserRound className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2} />
                      For Individuals
                    </button>
                    <button
                      type="button"
                      onClick={() => setType("business")}
                      className={`flex flex-1 items-center justify-center gap-2 border-l border-[#e6dfcf] px-3 py-2 text-[11px] font-medium transition sm:gap-2.5 sm:px-4 sm:text-[13px] lg:px-4 lg:py-2.5 lg:text-[14px] ${
                        type === "business"
                          ? "border border-[#f4b400] bg-[#fffdf4] text-[#21582e] shadow-[inset_0_-3px_0_#f4b400]"
                          : "text-[#121212]"
                      }`}
                    >
                      <BriefcaseBusiness className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2} />
                      For Business
                    </button>
                  </div>

                  <form className="mt-3.5 space-y-2 lg:mt-4" onSubmit={handleSubmit(onSubmit)}>
                    <div className="rounded-[14px] border border-[#ebe6da] bg-white px-3 py-2 shadow-[0_12px_32px_rgba(15,23,42,0.04)]">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-[#f6f8eb] text-[#345d32] sm:h-9 sm:w-9">
                          <UserRound className="h-4.5 w-4.5" strokeWidth={1.8} />
                        </div>
                        <div className="w-full">
                          <label className="block text-[11px] font-medium text-[#161616] sm:text-[12px]">
                            Full Name
                          </label>
                          <input
                            type="text"
                            {...register("fullname", {
                              required: "Full name is required",
                              validate: (value) => value.trim().length >= 2 || "Name must be at least 2 characters",
                              minLength: {
                                value: 2,
                                message: "Name must be at least 2 characters",
                              },
                            })}
                            placeholder="Enter your full name"
                            className="mt-0.5 h-[24px] w-full border-none bg-transparent p-0 text-[12px] text-[#20242f] outline-none placeholder:text-[#9aa0ad] sm:text-[13px]"
                          />
                        </div>
                      </div>
                      {errors.fullname && (
                        <p className="mt-3 text-sm text-red-500">{errors.fullname.message}</p>
                      )}
                    </div>

                    <div className="rounded-[14px] border border-[#ebe6da] bg-white px-3 py-2 shadow-[0_12px_32px_rgba(15,23,42,0.04)]">
                      <div className="flex items-start gap-2.5">
                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-[#f6f8eb] text-[#345d32] sm:h-9 sm:w-9">
                          <Phone className="h-4.5 w-4.5" strokeWidth={1.8} />
                        </div>
                        <div className="w-full">
                          <label className="block text-[11px] font-medium text-[#161616] sm:text-[12px]">
                            Phone Number
                          </label>
                          <div className="mt-1.5 flex items-center gap-2">
                            <div className="flex h-[34px] min-w-[64px] items-center justify-center gap-1 rounded-[9px] border border-[#e2ddd0] bg-white px-2.5 text-[12px] font-medium text-[#171717] sm:h-[36px] sm:min-w-[70px] sm:text-[13px]">
                              <span>+91</span>
                              <ChevronDown className="h-4 w-4" strokeWidth={2.5} />
                            </div>
                            <input
                              type="text"
                              {...register("mobileno", {
                                required: "Phone number is required",
                                pattern: {
                                  value: /^\d{5}\s\d{5}$/,
                                  message:
                                    "Invalid phone number format (e.g., 66666 66667)",
                                },
                              })}
                              onChange={handlePhoneChange}
                              placeholder="999999999"
                              maxLength={11}
                              className="h-[34px] w-full rounded-[9px] border border-[#e2ddd0] bg-white px-3 text-[12px] text-[#20242f] outline-none placeholder:text-[#9aa0ad] sm:h-[36px] sm:text-[13px]"
                            />
                          </div>
                          {errors.mobileno ? (
                            <p className="mt-3 text-sm text-red-500">{errors.mobileno.message}</p>
                          ) : (
                            <p className="mt-1 text-[9px] leading-3.5 text-[#6b7280] sm:text-[10px]">
                              Type correct number, we will verify using OTP.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="rounded-[14px] border border-[#ebe6da] bg-white px-3 py-2 shadow-[0_12px_32px_rgba(15,23,42,0.04)]">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-[#f6f8eb] text-[#345d32] sm:h-9 sm:w-9">
                          <Gift className="h-4.5 w-4.5" strokeWidth={1.8} />
                        </div>
                        <div className="w-full">
                          <label className="block text-[11px] font-medium text-[#161616] sm:text-[12px]">
                            Referral Code (Optional)
                          </label>
                          <input
                            type="text"
                            {...register("referralCode")}
                            placeholder="Enter referral code if any"
                            className="mt-1.5 h-[34px] w-full rounded-[9px] border border-[#e2ddd0] bg-white px-3 text-[12px] text-[#20242f] outline-none placeholder:text-[#9aa0ad] sm:h-[36px] sm:text-[13px]"
                          />
                        </div>
                      </div>
                    </div>
                    <label className="flex items-start gap-2 text-xs text-slate-700">
                      <input type="checkbox" {...register("acceptedTerms", { required: "Please accept the terms and privacy policy" })} />
                      <span>I agree to the <a className="text-green-700 underline" href="/terms-and-conditions/customer" target="_blank" rel="noreferrer">Terms and Conditions</a> and <a className="text-green-700 underline" href="/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>.</span>
                    </label>
                    {errors.acceptedTerms && <p className="text-sm text-red-500">{errors.acceptedTerms.message}</p>}

                    <button
                      type="submit"
                      disabled={loading}
                      className="flex h-[40px] w-full items-center justify-center gap-2 rounded-[12px] bg-gradient-to-r from-[#ff9800] to-[#ffac00] text-[13px] font-medium text-white shadow-[0_16px_30px_rgba(255,170,0,0.28)] transition hover:from-[#f39200] hover:to-[#ffab00] disabled:cursor-not-allowed disabled:opacity-70 sm:h-[44px] sm:text-[14px] lg:h-[46px]"
                    >
                      <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
                      {loading ? "Sending OTP..." : "Send OTP"}
                    </button>
                  </form>

                  <div className="mt-2.5 text-center text-[12px] text-[#1e1e1e] sm:text-[13px]">
                    <span>Already have an account? </span>
                    <button
                      onClick={() => {
                        setShowSignupModal(false);
                        setShowSigninModal(true);
                      }}
                      className="font-semibold text-[#1f5d29] transition hover:text-[#16471f]"
                    >
                      Sign In
                    </button>
                  </div>
                </div>
              </div>

              <div className="relative hidden min-h-0 overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.95),_rgba(255,252,244,0.86)_46%,_rgba(255,248,228,0.82)_100%)] lg:block">
                <img
                  src="/assets/delivery_man_images/sign_up.png"
                  alt="Delivery partner"
                  className="h-full w-full object-cover object-[center_12%] xl:object-center"
                />

                <div className="absolute bottom-0 left-0 right-0 rounded-tl-[28px] border-t border-l border-[#f1cc79] bg-[#2c6b35] px-4 pb-4 pt-4 text-white shadow-[0_-10px_30px_rgba(0,0,0,0.18)]">
                  <div className="space-y-3">
                    {rightPanelBenefits.map(({ icon: Icon, title, description }) => (
                      <div key={title} className="flex items-center gap-2.5">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-[#2c6b35] shadow-[0_8px_18px_rgba(0,0,0,0.14)]">
                          <Icon className="h-4.5 w-4.5" strokeWidth={2} />
                        </div>
                        <div>
                          <p className="text-[13px] font-medium leading-4">{title}</p>
                          <p className="mt-0.5 text-[11px] leading-4 text-white/88">
                            {description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
